import { browser } from '$app/environment';
import { createCheckConsentForVendor } from '$lib/services/tracking/didomi/consent';

interface TrackerValue<T> {
	method: (event: T) => void;
	arg: T;
}
type Queue<T> = Map<string, TrackerValue<T>>;

interface Store<T> {
	queue: Queue<T>;
	isReady: boolean;
	hasConsent: boolean;
}

const stores = new Map<string, Store<any>>();

function getStore<T>(name: string): Store<T> {
	if (!stores.has(name)) {
		stores.set(name, { queue: new Map(), isReady: false, hasConsent: false });
	}
	return stores.get(name) as Store<T>;
}

const POLLING_INTERVAL = 100;
const POLLING_TIMEOUT = 6000;

export function useTrackingQueue<T>(queueName: string, vendorId: string, shouldCheckForConsent = true) {
	const store = getStore<T>(queueName);

	// Using passive polling because it's more efficient than using MutationObserver
	// and onload and onerror are not reliable when the script is already in DOM or loaded
	function passivePollingUntilReady(condition: () => boolean, timeout = POLLING_TIMEOUT) {
		if (!browser || typeof window === 'undefined') {
			return;
		}

		const start = Date.now();
		if (shouldCheckForConsent) {
			onConsentCallback(vendorId);
		} else {
			store.hasConsent = true;
		}

		const interval = setInterval(() => {
			if (condition() && store.hasConsent) {
				clearInterval(interval);
				onLoadCallback();
			} else if (Date.now() - start > timeout || !store.hasConsent) {
				clearInterval(interval);
				flushQueue();
			}
		}, POLLING_INTERVAL);
	}

	function flushQueue() {
		store.queue.clear();
	}

	function onConsentCallback(vendorId: string) {
		store.hasConsent = createCheckConsentForVendor(vendorId)();
	}

	function onLoadCallback() {
		store.isReady = true;

		if (store.queue.size > 0) {
			store.queue.forEach(({ method, arg }, key) => {
				method(arg);
				store.queue.delete(key);
			});
		}
	}

	function runEvent(method: (event: T) => void, arg: T) {
		if (!method || typeof method !== 'function' || !arg || !browser) {
			return;
		}

		if (!store.isReady) {
			store.queue.set(`${queueName}-${store.queue.size + 1}`, { method, arg });
			return;
		}

		method(arg);
	}

	return {
		passivePollingUntilReady,
		runEvent,
		onConsentCallback,
	};
}
